import React, { useState, useReducer, Reducer, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import Header from './header'
import Footer from './footer'
import reducer, { Moments, MomentCardState, CardState } from './reducer'

import type { CardEditorForm, ComponentCardForm } from '../../types/cardForm'
import Report from '../../types/api/report'
import onSave from './onSave'
import { resolveValidations } from '../../../Form/validations'

import GlobalNotification from 'live/services/global-notification'
import { Notification } from '../../../common/components/Notification/Notification'
import i18n from 'live/services/i18n'
import { Collapse } from 'react-bootstrap'
import { extractLocationObj } from '../../helper/extractLocationObj'
import { callOptionalFc } from '../../helper/callOptionalFc'

export interface ActionHeaderProps<Type> {
    form: Type
    onHide: () => void
    onChange: (form: Type) => void
}

export interface ActionHeaderIconeProps<Type> {
    onClick: () => void
    form: Type
}

export interface ActionHeader<Type> {
    iconeClass?: string
    icone?: React.FunctionComponent<ActionHeaderIconeProps<Type>>
    actionComponent: React.FunctionComponent<ActionHeaderProps<Type>>
    hideOnDisplayMode?: boolean
}

interface Props<T, Y = Report> extends CardEditorForm<T, Y> {
    onChange: (report: Y) => void
    FormComponent: React.FunctionComponent<ComponentCardForm<T>>
    hideHeader?: boolean
    variant?: 'primary' | 'secondary'
    className?: string
    hideFooter?: boolean
    validations?: string[]
    actionsHeader?: ActionHeader<T>[]
    collapseStartsClosed?: boolean
    collapseOnHideClosed?: boolean
    reloadEditMode?: boolean
}

interface CardDecorator<T> {
    title: string
    get: (report: Report) => T
    set: (changed: T, report: Report) => Report
    actionsHeader?: ActionHeader<T>[]
    className?: string
    collapseStartsClosed?: boolean
    collapseOnHideClosed?: boolean
}

interface CardDecoratorBase {
    report: Report
    onChange: (report: Report) => void
    useRouter?: boolean
}

interface CardDecoratorDefault extends CardDecoratorBase {
    useRouter?: false
}

interface CardDecoratorSearchHistory extends CardDecoratorBase {
    useRouter: true
    routerMethods: {
        eventOnLoadEditMode: (history: any, urlSearch: { [key: string]: string }) => boolean
        eventOnHide: (history: any, urlSearch: { [key: string]: string }) => void
        eventOnOpen: (history: any, urlSearch: { [key: string]: string }) => void
    }
}

type TypeCardDecorator = CardDecoratorSearchHistory | CardDecoratorDefault

export function card<T>({
    className,
    get,
    set,
    title,
    actionsHeader,
    collapseStartsClosed,
    collapseOnHideClosed
}: CardDecorator<T>) {
    return function (Component: React.FunctionComponent<ComponentCardForm<T>>) {
        return function CardDecorator({ report, onChange, useRouter, ...props }: TypeCardDecorator): JSX.Element {
            const [isNew, setIsNew] = useState<boolean>(!report?.uid)
            const history = useHistory()

            useEffect(() => {
                if (useRouter) {
                    const { routerMethods } = props as CardDecoratorSearchHistory
                    const eventOnLoadEditMode = routerMethods?.eventOnLoadEditMode
                    if (typeof eventOnLoadEditMode == 'function') {
                        const e = eventOnLoadEditMode(history, extractLocationObj(history?.location?.search))
                        setIsNew(e)
                    }
                }
            }, [])

            return (
                <CardEditor<T>
                    className={className}
                    onChange={updatedReport => {
                        onChange(updatedReport)
                        if (isNew && !report?.uid) {
                            history.push('/generalinformation/' + updatedReport.uid)
                            setIsNew(false)
                        }
                    }}
                    eventOnHide={() => {
                        if (useRouter) {
                            const { routerMethods } = props as CardDecoratorSearchHistory
                            if (typeof routerMethods?.eventOnHide == 'function') {
                                routerMethods?.eventOnHide(history, extractLocationObj(history?.location?.search))
                            }
                        }
                    }}
                    eventOnOpen={() => {
                        if (useRouter) {
                            const { routerMethods } = props as CardDecoratorSearchHistory
                            if (typeof routerMethods?.eventOnOpen == 'function') {
                                routerMethods?.eventOnOpen(history, extractLocationObj(history?.location?.search))
                            }
                        }
                    }}
                    FormComponent={Component}
                    formObject={get(report)}
                    onSave={newProps => {
                        return onSave<T>(report, newProps, set)
                    }}
                    defaultEditMode={isNew}
                    title={title}
                    actionsHeader={actionsHeader}
                    collapseStartsClosed={collapseStartsClosed}
                    collapseOnHideClosed={collapseOnHideClosed}
                    reloadEditMode={useRouter}
                ></CardEditor>
            )
        }
    }
}

export default function CardEditor<T, Y = Report>({
    FormComponent,
    onSave,
    onChange,
    onCancel,
    formObject,
    title,
    defaultEditMode = false,
    hideHeader = false,
    variant = 'primary',
    className,
    actionsHeader = [],
    collapseStartsClosed,
    reloadEditMode = false,
    collapseOnHideClosed = false,
    eventOnCancel,
    eventOnHide,
    eventOnSave,
    eventOnOpen
}: Props<T, Y>): JSX.Element {
    const [validations, setValidations] = useState<string[]>([])
    const [collapseOpen, setCollapseOpen] = useState<boolean>(true) //!collapseOnHideClosed
    const [state, dispatch] = useReducer<Reducer<CardState<T>, Moments<T>>>(reducer, {
        draftForm: formObject,
        editMode: defaultEditMode,
        loading: false,
        events: {
            eventOnClose() {
                callOptionalFc(eventOnCancel)
            },
            eventOnHide() {
                if (collapseOnHideClosed && !collapseOpen) {
                    setCollapseOpen(!collapseOpen)
                }
                callOptionalFc(eventOnHide)
            },
            eventOnOpen() {
                callOptionalFc(eventOnOpen)
            },
            eventOnSave() {
                callOptionalFc(eventOnSave)
            }
        }
    } as CardState<T>)

    useMemo(() => {
        if (reloadEditMode && defaultEditMode != state.editMode) {
            if (defaultEditMode) {
                dispatch({ type: MomentCardState.TO_EDIT })
            }
        }
    }, [defaultEditMode])
    useEffect(() => {
        try {
            setValidations(resolveValidations(title, state.draftForm))
        } catch (err) {
            console.error(err)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.draftForm])

    useEffect(() => {}, [validations])

    useEffect(() => {
        setCollapseOpen(!collapseStartsClosed)
    }, [])

    useEffect(() => {
        if (state.editMode) setCollapseOpen(true)
    }, [state.editMode])

    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    if (!FormComponent) return null

    const save = (): void => {
        dispatch({ type: MomentCardState.LOADING })

        if (!validations?.length) {
            onSave(state.draftForm)
                .then(report => onChange(report))
                .finally(() => {
                    dispatch({ type: MomentCardState.SAVE })
                })
        } else {
            GlobalNotification.error(
                <Notification title={i18n('Before saving, you need to fix the following issues: ')}>
                    {i18n('The following entered fields are invalid: ')}
                    {validations.map((value, index) => (
                        <tr key={index}>{value}</tr>
                    ))}
                </Notification>,
                20
            )
        }
    }

    return (
        <StyledCardEditor editMode={state.editMode} className={`cardEditor ${variant} ${className}`}>
            {hideHeader ? null : (
                <Header<T>
                    title={title}
                    editMode={state.editMode}
                    onChange={(form: T) => {
                        dispatch({ form, type: MomentCardState.UPDATE })
                    }}
                    onEdit={() => dispatch({ type: MomentCardState.TO_EDIT })}
                    actionsHeader={actionsHeader}
                    collapseSettings={
                        collapseStartsClosed !== undefined
                            ? {
                                  open: collapseOpen,
                                  changeDirection: () => {
                                      setCollapseOpen(!collapseOpen)
                                  }
                              }
                            : undefined
                    }
                    form={state.draftForm}
                ></Header>
            )}
            <Collapse in={collapseOpen}>
                <div style={{ overflow: 'visible' }}>
                    <div className={`cardEditor--content`}>
                        <FormComponent
                            onEditMode={() => {
                                dispatch({ type: MomentCardState.TO_EDIT })
                            }}
                            onCancel={() => {
                                dispatch({ form: formObject, type: MomentCardState.CANCEL })
                            }}
                            setErrorMessage={setErrorMessage}
                            errorMessage={errorMessage}
                            editMode={state.editMode}
                            formObject={state.draftForm}
                            validations={validations || []}
                            onChange={(form: T) => {
                                dispatch({ form, type: MomentCardState.UPDATE })
                            }}
                        />
                    </div>
                    <Footer
                        loading={state.loading}
                        variant={variant}
                        onCancel={() => {
                            dispatch({ form: formObject, type: MomentCardState.CANCEL })
                            if (onCancel) {
                                onCancel()
                            }
                            if (errorMessage) setErrorMessage('')
                        }}
                        onSave={save}
                        errorMessage={errorMessage}
                        editMode={state.editMode}
                    />
                </div>
            </Collapse>
        </StyledCardEditor>
    )
}

const StyledCardEditor = styled.div<{ editMode? }>`
    border: 1px solid ${({ theme, editMode }) => (editMode ? theme['blue-500'] : theme['gray-400'])};
    ${({ theme, editMode = false }) => {
        if (editMode === true) {
            return `background: ${theme['gray-100']};`
        }
    }}
    border-radius: 3px;
    margin-top: 10px;
    background: ${({ theme }) => theme.white};

    .cardEditor {
        &--content {
            border-top: 1px solid ${({ theme }) => theme['gray-400']};
        }
        &.secondary {
            border: none;
            border-radius: none;
            .cardEditor--content {
                border-top: none;
                max-height: 50vh;
            }
        }
    }

    &.emailBodyConfiguration,
    &.emailPDFBodyConfiguration {
        background: ${({ theme, editMode }) => (editMode ? theme['gray-100'] : theme.white)};
        border-radius: 4px;
        /* &.Container {
            border: 1px solid ${({ theme, editMode }) => (editMode ? theme['gray-100'] : theme['gray-400'])};
        } */
    }
`
export { StyledCardEditor }
